import React from "react";
import { useTranslation } from "react-i18next";
import type { Hint } from "./useSettingsHints";

interface SettingHintProps {
  hint: Hint;
  /** Click sur "Utiliser « X » →" — propage `hint.applyValue` au parent */
  onApplySuggestion?: (key: Hint["key"], value: unknown) => void;
}

/**
 * Bandeau inline jaune dans une `SettingRow` du groupe Suggestions.
 * Affiche le message + un lien cliquable "Utiliser « X » →" si une valeur
 * suggérée est calculée. Cf. plan §Hints + Pre-publish Modal B.html
 * lignes 208-225.
 */
const SettingHint: React.FC<SettingHintProps> = ({
  hint,
  onApplySuggestion,
}) => {
  const { t } = useTranslation();
  const isInfo = hint.tone === "info";
  return (
    <div
      data-testid={`setting-hint-${hint.key}`}
      data-tone={hint.tone}
      className={
        isInfo
          ? "mt-2 rounded-[7px] border border-primary/30 bg-primary/[0.08] px-2.5 py-2 text-[11px] text-foreground/85"
          : "mt-2 rounded-[7px] border border-warning/30 bg-warning/[0.12] px-2.5 py-2 text-[11px] text-foreground/85"
      }
    >
      <span aria-hidden className="mr-1">
        💡
      </span>
      <span>{hint.message}</span>
      {hint.applyValue !== undefined && hint.suggestionLabel && (
        <>
          <button
            type="button"
            data-testid={`setting-hint-${hint.key}-apply`}
            onClick={() => onApplySuggestion?.(hint.key, hint.applyValue)}
            className="ml-1.5 inline cursor-pointer text-[11px] font-semibold text-primary hover:underline"
          >
            {t("use_label", { label: hint.suggestionLabel })}
          </button>
        </>
      )}
    </div>
  );
};

export default SettingHint;
